import { CheckCircle2, Clock3, Loader2, TicketCheck } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useOAuthStore } from '@/features/auth/store';
// the flags module only: the feature barrel also carries the flow's dialogs.
import { readFlowFlags } from '@/features/flow/flagsStore';
import type { OnboardingStatus } from '@/lib/api/onboarding';

interface VoucherQueueStepProps {
    status: OnboardingStatus;
    busy: boolean;
    error: string | null;
    onRedeem: (code: string) => void | Promise<void>;
    onReady: () => void | Promise<void>;
    onRejoin: () => void | Promise<void>;
    onContinue: () => void;
}

function ReadyCountdown({ expiresAt }: { expiresAt: string | null }) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 1_000);
        return () => window.clearInterval(timer);
    }, []);
    const seconds = Math.max(
        0,
        Math.ceil(
            ((expiresAt ? new Date(expiresAt).getTime() : now) - now) / 1_000,
        ),
    );
    return <span className="font-mono text-primary">{seconds}s</span>;
}

const VoucherQueueStep: React.FC<VoucherQueueStepProps> = ({
    status,
    busy,
    error,
    onRedeem,
    onReady,
    onRejoin,
    onContinue,
}) => {
    const accountHash = useOAuthStore((state) =>
        state.me?.authenticated ? (state.me.accountHash ?? null) : null,
    );
    // a launch code this account already validated in the feature-flags view
    const [code, setCode] = useState(
        () => readFlowFlags(accountHash)?.v1?.code ?? '',
    );
    const isReady = status.state === 'ready' || status.state === 'admitted';
    // A cancelled entry is not a dead end: the backend lets it be replaced by
    // redeeming again, which spends a use of whatever code is entered.
    const showCodeEntry =
        status.state === 'notRedeemed' || status.state === 'cancelled';

    return (
        <div className="flex flex-col items-center px-6 pb-6 pt-2 text-center">
            <div className="flex size-16 items-center justify-center rounded-2xl border border-border bg-secondary/50">
                <TicketCheck size={30} className="text-primary" />
            </div>
            <h2 className="mt-4 text-xl font-bold tracking-tight text-foreground">
                Launch access
            </h2>

            {showCodeEntry && (
                <div className="mt-5 w-full text-left">
                    {status.state === 'cancelled' && (
                        <p className="mb-3 text-sm text-destructive">
                            Your previous queue entry was cancelled by an
                            administrator.
                        </p>
                    )}
                    <p className="text-sm leading-relaxed text-muted-foreground">
                        Enter a launch voucher to join the wallet setup line.
                        Your place is saved, so you can minimize this window.
                    </p>
                    <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Voucher code
                        <input
                            value={code}
                            onChange={(event) => setCode(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && code.trim())
                                    void onRedeem(code);
                            }}
                            autoComplete="off"
                            // The field renders uppercase, so the keyboard
                            // should type it that way; autocorrect on a voucher
                            // code only ever mangles it.
                            autoCapitalize="characters"
                            autoCorrect="off"
                            spellCheck={false}
                            enterKeyHint="go"
                            className="mt-2 h-11 w-full rounded-lg border border-border bg-background px-3 font-mono text-sm uppercase text-foreground outline-none focus:border-primary"
                            placeholder="SNAPPY-…"
                        />
                    </label>
                    <Button
                        className="mt-3 w-full"
                        disabled={busy || code.trim().length < 6}
                        onClick={() => void onRedeem(code)}
                    >
                        {busy ? 'Checking…' : 'Join the line'}
                    </Button>
                </div>
            )}

            {status.state === 'queued' && (
                <div className="mt-5 w-full rounded-xl border border-border bg-background/50 p-5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Your position
                    </p>
                    <p className="mt-1 font-mono text-4xl font-bold text-foreground">
                        #{status.position}
                    </p>
                    <p className="mt-3 text-sm text-muted-foreground">
                        {status.estimatedWaitMinutes
                            ? `${status.estimatedWaitMinutes.min} to ${status.estimatedWaitMinutes.max} minutes estimated`
                            : 'Waiting for wallet setup capacity'}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Keep Snappy open. We’ll show a two-minute ready check.
                    </p>
                </div>
            )}

            {isReady && (
                <div className="mt-5 w-full rounded-xl border border-primary/40 bg-primary/5 p-5">
                    <Clock3 className="mx-auto text-primary" />
                    <p className="mt-3 text-lg font-bold text-foreground">
                        Your lane is ready
                    </p>
                    {status.state === 'ready' && (
                        <p className="mt-1 text-sm text-muted-foreground">
                            Confirm within{' '}
                            <ReadyCountdown expiresAt={status.readyExpiresAt} />
                        </p>
                    )}
                    <Button
                        className="mt-4 w-full"
                        disabled={busy}
                        onClick={() => void onReady()}
                    >
                        {busy
                            ? 'Creating wallet…'
                            : status.state === 'admitted'
                                ? 'Retry wallet setup'
                                : "I'm ready"}
                    </Button>
                </div>
            )}

            {status.state === 'provisioning' && (
                <div className="mt-8 flex flex-col items-center">
                    <Loader2 className="animate-spin text-primary" size={30} />
                    <p className="mt-3 font-semibold text-foreground">
                        Creating your Snappy Wallet
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                        This usually takes a few seconds.
                    </p>
                </div>
            )}

            {status.state === 'completed' && (
                <div className="mt-6 w-full">
                    <CheckCircle2 className="mx-auto text-success" size={34} />
                    <p className="mt-3 text-lg font-bold text-foreground">
                        Your wallet is ready
                    </p>
                    <Button className="mt-5 w-full" onClick={onContinue}>
                        Continue to funding
                    </Button>
                </div>
            )}

            {status.state === 'missed' && (
                <div className="mt-6 w-full">
                    <p className="text-sm text-muted-foreground">
                        The ready window expired. Your voucher remains valid;
                        rejoin at the back of the line.
                    </p>
                    <Button
                        className="mt-4 w-full"
                        disabled={busy}
                        onClick={() => void onRejoin()}
                    >
                        Rejoin the line
                    </Button>
                </div>
            )}

            {status.state === 'disabled' && (
                <div className="mt-6 w-full">
                    <p className="text-sm text-muted-foreground">
                        Launch vouchers are currently turned off.
                    </p>
                    <Button className="mt-4 w-full" onClick={onContinue}>
                        Continue
                    </Button>
                </div>
            )}

            {error && (
                <p role="alert" className="mt-4 text-sm text-destructive">
                    {error}
                </p>
            )}
        </div>
    );
};

export default VoucherQueueStep;
